import { ClaimType } from "../../store-management/claims/claim-model";

/** Payment status for billing service overview (`GET /billing/service-overview`). */
export type BillingOverviewPaymentStatus =
  | "none"
  | "ok"
  | "past_due"
  | "unpaid";

/** Service status for billing service overview (`GET /billing/service-overview`). */
export type BillingOverviewServiceStatus =
  | "not_activated"
  | "active"
  | "ending_soon"
  | "suspended"
  | "ended";

export interface BillingServiceOverviewPeriod {
  startDate: string;
  endDate: string;
  daysRemaining: number;
  renewalDate: string;
}

export type BillingServiceOverviewClaimCounts = Record<ClaimType, number>;

export interface BillingServiceOverviewUsage {
  claimCount: number;
  claimCountByType: BillingServiceOverviewClaimCounts;
}

export interface BillingServiceOverviewPlan {
  tier: number;
  priceId: string;
  amountCents: number;
  currency: string;
  minClaims: number;
  maxClaims: number | null;
}

/** Raw subscribed plan from API — tier/amount/range may be null when not resolved yet. */
export interface BillingServiceOverviewSubscribedPlanResponse {
  tier: number | null;
  priceId: string;
  amountCents: number | null;
  currency: string;
  minClaims?: number;
  maxClaims?: number | null;
}

/** API payload for `GET /billing/service-overview`. */
export interface BillingServiceOverviewApiResponse
  extends Omit<BillingServiceOverview, "subscribedPlan"> {
  subscribedPlan: BillingServiceOverviewSubscribedPlanResponse;
}

export interface BillingServiceOverviewRenewal {
  amountCents: number;
  currency: string;
  tier: number;
  date: string;
}

export interface BillingServiceOverviewFlags {
  cancelAtPeriodEnd: boolean;
  inTrial: boolean;
}

export interface BillingServiceOverview {
  serviceStatus: BillingOverviewServiceStatus;
  paymentStatus: BillingOverviewPaymentStatus;
  currentPeriod: BillingServiceOverviewPeriod;
  usage: BillingServiceOverviewUsage;
  /** Projected plan for the current period based on usage. */
  currentServicePlan: BillingServiceOverviewPlan;
  /** Plan the customer is subscribed to for this period. */
  subscribedPlan: BillingServiceOverviewPlan;
  estimatedRenewal: BillingServiceOverviewRenewal;
  previousPeriod: BillingServiceOverviewPeriod | null;
  flags: BillingServiceOverviewFlags;
}

export const BILLING_OVERVIEW_CLAIM_TYPE_ORDER: readonly ClaimType[] = [
  ClaimType.Lost_Outbound,
  ClaimType.Adjustments_Damaged,
  ClaimType.Lost_Inbound,
  ClaimType.Refunds_Never_Returned_FBA,
  ClaimType.ReAnalysis,
  ClaimType.Wrong_FBA_Fees,
  ClaimType.Unknown,
] as const;

export interface BillingBreakdownChartSlice {
  claimTypeValue: string;
  count: number;
  percentage: number;
  color: string;
}
